// @flow
import { createSelector } from 'reselect';
import { CAMPAIGN } from 'services/actions/campaignActions';
import type { Action } from 'services/actions/Action.type';
import type { State } from './index';

const TWO_WINDOW = 'two_window';
const WINDOW_ONE = 'window_one';
const WINDOW_TWO = 'window_two';

export type CampaignState = {|
  +premiumStartDate: string,
  +premiumEndDate: string,
  +startDate: string,
  +endDate: string,
  // TODO: update categories type
  +fetchingCampaign: boolean,
  +campaignName: string | null,
  +campaignId: number | null,
  +campaignStatus: string | null,
  +campaignWindow: number,
  +windowType: string | null,
  +userAccess: boolean,
  +sponsoredThreshold: number,
  +bannerTitle: string,
  +bannerParagraph: string,
  +bannerBackgroundColor: string,
  +bannerFAQUrl: string,
  +bannerDesktopImageUrl: string,
  +socialWindow: number | null,
  +userSocialAccess: boolean,
|};

export const campaignInitialState = {
  premiumStartDate: '1970-01-01T00:00:00Z',
  premiumEndDate: '2020-01-01T00:00:00Z',
  startDate: '1970-01-01T00:00:00Z',
  endDate: '2020-01-01T00:00:00Z',
  fetchingCampaign: true,
  campaignName: null,
  campaignId: null,
  campaignStatus: null,
  windowType: null,
  userAccess: false,
  sponsoredThreshold: 0,
  campaignError: false,
  bannerTitle: '',
  bannerParagraph: '',
  bannerBackgroundColor: '',
  bannerFAQUrl: '',
  bannerDesktopImageUrl: '',
  socialWindow: null,
  userSocialAccess: false,
};

export function campaignReducer(
  state: CampaignState = campaignInitialState,
  action: Action
): CampaignState {
  switch (action.type) {
    case CAMPAIGN.REQUEST_ALL_DATA: {
      return {
        ...state,
        fetchingCampaign: true,
      };
    }
    case CAMPAIGN.RECEIVE_CAMPAIGN: {
      const {
        premiumStartDate,
        premiumEndDate,
        startDate,
        endDate,
        id,
        name,
        campaignStatus,
        campaignWindow,
        windowType,
        userAccess,
        sponsoredThreshold,
        bannerTitle,
        bannerParagraph,
        bannerBackgroundColor,
        bannerFAQUrl,
        bannerDesktopImageUrl,
        socialWindow,
        userSocialAccess,
      } = action.payload;
      return {
        ...state,
        premiumStartDate,
        premiumEndDate,
        startDate,
        endDate,
        userAccess,
        campaignStatus: campaignStatus.toLowerCase(),
        campaignWindow,
        windowType: windowType.toLowerCase(),
        fetchingCampaign: false,
        campaignId: id,
        campaignName: name,
        sponsoredThreshold,
        bannerTitle,
        bannerParagraph,
        bannerBackgroundColor,
        bannerFAQUrl,
        bannerDesktopImageUrl,
        socialWindow,
        userSocialAccess,
      };
    }
    case CAMPAIGN.RECEIVE_CAMPAIGN_ERROR: {
      return {
        ...state,
        campaignError: true,
      };
    }
    default: {
      return state;
    }
  }
}

// Selectors
// selectIsCampaignOpen: selector to validate if user has access to the sale
export const selectIsCampaignOpen: (State) => boolean = createSelector(
  (state) => state.campaignState.campaignStatus,
  (state) => state.campaignState.windowType,
  (state) => state.campaignState.userAccess,
  (campaignStatus, windowType, userAccess) => {
    const isTwoWindowCampaign = windowType === TWO_WINDOW;
    let isCampaignOpen = false;

    if (isTwoWindowCampaign) {
      switch (campaignStatus) {
        case WINDOW_ONE:
          isCampaignOpen = userAccess;
          break;
        case WINDOW_TWO:
          isCampaignOpen = userAccess;
          break;
        default: // do nothing
      }
    } else {
      // this case is for campaign that is a split access
      isCampaignOpen = userAccess;
    }

    return isCampaignOpen;
  }
);

// selectCampaignEndDate: get campaign end date based on windowType and campaignStatus
export const selectCampaignEndDate: (State) => string = createSelector(
  (state) => state.campaignState.campaignStatus,
  (state) => state.campaignState.windowType,
  (state) => state.campaignState.premiumEndDate,
  (state) => state.campaignState.endDate,
  (campaignStatus, windowType, premiumEndDate, endDate) => {
    const isTwoWindowCampaign = windowType === TWO_WINDOW;

    if (isTwoWindowCampaign && campaignStatus === WINDOW_ONE) {
      return premiumEndDate;
    }
    return endDate;
  }
);
